<script setup lang="ts">
import zhCn from 'element-plus/dist/locale/zh-cn.mjs'
import en from 'element-plus/dist/locale/en.mjs'
import vi from 'element-plus/dist/locale/vi.mjs'
import './baseInit'
import { locale } from './hooks/system/useVuei18n'

const setting = computed(() => {
  switch (locale.value) {
    case 'CMN':
      return zhCn
    case 'VIE':
      return vi
    case 'ZHO':
      return zhCn
    default:
      return en
  }
})

const barRef = ref<HTMLElement | null>(null)
const thumbRef = ref<HTMLElement | null>(null)
const visible = ref(false)
const dragging = ref(false)
let startY = 0
let startScrollTop = 0
let hideTimer: ReturnType<typeof setTimeout> | null = null

function updateThumb() {
  const { scrollHeight, clientHeight, scrollTop } = document.documentElement
  if (scrollHeight <= clientHeight) {
    visible.value = false
    return
  }
  const ratio = clientHeight / scrollHeight
  const thumbH = Math.max(ratio * clientHeight, 30)
  const thumbTop = (scrollTop / (scrollHeight - clientHeight)) * (clientHeight - thumbH)
  if (thumbRef.value) {
    thumbRef.value.style.height = `${thumbH}px`
    thumbRef.value.style.transform = `translateY(${thumbTop}px)`
  }
}

function showBar() {
  visible.value = true
  if (hideTimer) clearTimeout(hideTimer)
  hideTimer = setTimeout(() => {
    if (!dragging.value) visible.value = false
  }, 1500)
}

function onScroll() {
  updateThumb()
  showBar()
}

function onMousedown(e: MouseEvent) {
  e.preventDefault()
  dragging.value = true
  startY = e.clientY
  startScrollTop = document.documentElement.scrollTop
  document.addEventListener('mousemove', onMousemove)
  document.addEventListener('mouseup', onMouseup)
}

function onMousemove(e: MouseEvent) {
  const { scrollHeight, clientHeight } = document.documentElement
  const ratio = clientHeight / scrollHeight
  const thumbH = Math.max(ratio * clientHeight, 30)
  const maxScroll = scrollHeight - clientHeight
  const maxThumbMove = clientHeight - thumbH
  const delta = e.clientY - startY
  document.documentElement.scrollTop = startScrollTop + (delta / maxThumbMove) * maxScroll
}

function onMouseup() {
  dragging.value = false
  document.removeEventListener('mousemove', onMousemove)
  document.removeEventListener('mouseup', onMouseup)
  showBar()
}

function onBarMousedown(e: MouseEvent) {
  if (e.target === thumbRef.value) return
  const { scrollHeight, clientHeight } = document.documentElement
  const ratio = clientHeight / scrollHeight
  const thumbH = Math.max(ratio * clientHeight, 30)
  const maxScroll = scrollHeight - clientHeight
  const maxThumbMove = clientHeight - thumbH
  const barRect = barRef.value!.getBoundingClientRect()
  const targetTop = e.clientY - barRect.top - thumbH / 2
  document.documentElement.scrollTop = (targetTop / maxThumbMove) * maxScroll
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', updateThumb)
  updateThumb()
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)
  window.removeEventListener('resize', updateThumb)
  document.removeEventListener('mousemove', onMousemove)
  document.removeEventListener('mouseup', onMouseup)
  if (hideTimer) clearTimeout(hideTimer)
})
</script>

<template>
  <el-config-provider :locale="setting">
    <router-view />
  </el-config-provider>
  <div
    v-show="visible"
    ref="barRef"
    class="custom-scrollbar"
    @mousedown.self="onBarMousedown"
  >
    <div ref="thumbRef" class="custom-scrollbar-thumb" @mousedown.prevent="onMousedown" />
  </div>
</template>

<style lang="less">
body {
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}
</style>

<style lang="less" scoped>
.custom-scrollbar {
  position: fixed;
  top: 0;
  right: 4px;
  z-index: 99999;
  width: 6px;
  height: 100vh;
  background: transparent;

  &-thumb {
    position: absolute;
    top: 0;
    width: 100%;
    min-height: 30px;
    cursor: pointer;
    background: var(--bar);
    border-radius: 3px;
    transition: opacity .3s;

    &:hover {
      opacity: .8;
    }
  }
}
</style>
